import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, UtensilsCrossed, Newspaper, Ticket, Users,
  MapPin, Inbox, Settings as SettingsIcon, Plus, Pencil, Trash2, Check, X, LogOut, ExternalLink, ChevronUp, ChevronDown, BookOpen, Home,
} from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold">{label}</span>
    <input {...props} className="mt-1 w-full rounded-lg border-2 border-brand-ink bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-green" />
  </label>
);
const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold">{label}</span>
    <textarea {...props} className="mt-1 w-full rounded-lg border-2 border-brand-ink bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-green" />
  </label>
);

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-lg rounded-3xl border-2 border-brand-ink bg-brand-cream p-6 shadow-brutal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-2xl font-bold">{title}</h3>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border-2 border-brand-ink"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ImagePicker = ({ value, onChange, label = "Image" }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadFile(f)); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <span className="text-sm font-semibold">{label}</span>
      {value && <img src={fileUrl(value)} alt="" className="mt-1 h-24 w-32 rounded-lg border-2 border-brand-ink object-cover" />}
      <input type="file" accept="image/*" onChange={pick} className="mt-1 w-full text-sm" />
      {busy && <span className="text-xs text-muted-foreground">Uploading…</span>}
    </div>
  );
};

// ---------- Sections ----------
const Overview = ({ stats }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {[
      ["members", "Members"], ["products", "Products"], ["news", "News Articles"],
      ["pending_receipts", "Pending Receipts"], ["vouchers", "Vouchers"], ["franchise_inquiries", "Franchise Leads"],
    ].map(([k, label]) => (
      <div key={k} className="rounded-2xl border-2 border-brand-ink bg-white p-6 shadow-brutal">
        <p className="text-sm font-semibold text-muted-foreground">{label}</p>
        <p className="mt-2 font-display text-5xl font-black">{stats[k] ?? 0}</p>
      </div>
    ))}
  </div>
);

const Receipts = ({ refreshStats }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/receipts").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { const { data } = await api.post(`/receipts/${id}/${action}`); toast.success(action === "approve" ? `Approved · +${data.points_awarded} pts` : "Rejected"); load(); refreshStats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-receipts">
      {items.length === 0 && <p className="text-muted-foreground">No receipts.</p>}
      {items.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-4 rounded-2xl border-2 border-brand-ink bg-white p-4">
          <a href={fileUrl(r.image_path)} target="_blank" rel="noreferrer">
            <img src={fileUrl(r.image_path)} alt="receipt" onError={(e) => { e.currentTarget.src = "/panda-mascot.png"; }} className="h-16 w-16 rounded-lg border-2 border-brand-ink object-cover" />
          </a>
          <div className="flex-1">
            <p className="font-semibold">{r.user_name} <span className="text-xs text-muted-foreground">({r.user_email})</span></p>
            <p className="font-display text-xl font-bold">RM {r.amount?.toFixed(2)} <span className="text-sm font-normal">→ {Math.floor(r.amount)} pts</span></p>
          </div>
          {r.status === "pending" ? (
            <div className="flex gap-2">
              <button onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-brand-green px-4 py-2 text-sm font-bold hover-brutal"><Check className="h-4 w-4" />Approve</button>
              <button onClick={() => act(r.id, "reject")} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-red-500 px-4 py-2 text-sm font-bold text-white hover-brutal"><X className="h-4 w-4" />Reject</button>
            </div>
          ) : (
            <span className={`rounded-full border-2 border-brand-ink px-3 py-1 text-xs font-bold ${r.status === "approved" ? "bg-brand-green" : "bg-muted"}`}>{r.status}</span>
          )}
        </div>
      ))}
    </div>
  );
};

const Products = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get("/products").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setForm({ name: "", name_ms: "", description: "", description_ms: "", category: "signature", image_path: "", order: items.length }); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setOpen(true); };
  const save = async () => {
    try {
      if (form.id) await api.put(`/products/${form.id}`, form);
      else await api.post("/products", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/products/${id}`); load(); };
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[index], b = items[target];
    const ao = a.order ?? index, bo = b.order ?? target;
    try {
      await Promise.all([
        api.put(`/products/${a.id}`, { ...a, order: bo }),
        api.put(`/products/${b.id}`, { ...b, order: ao }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };
  return (
    <div data-testid="admin-products">
      <button onClick={openNew} data-testid="product-add" className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Product</button>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p, i) => (
          <div key={p.id} className="overflow-hidden rounded-2xl border-2 border-brand-ink bg-white">
            <img src={p.image_path ? fileUrl(p.image_path) : "https://images.unsplash.com/photo-1610970878459-a0e464d7592b?auto=format&fit=crop&w=800&q=80"} alt={p.name} className="aspect-[4/3] w-full object-cover" />
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="font-display text-lg font-bold">{p.name}</p>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`product-up-${p.id}`} aria-label="Move up" className="grid h-7 w-7 place-items-center rounded-lg border-2 border-brand-ink disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`product-down-${p.id}`} aria-label="Move down" className="grid h-7 w-7 place-items-center rounded-lg border-2 border-brand-ink disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <p className="line-clamp-1 text-xs text-muted-foreground">{p.description}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border-2 border-brand-ink px-3 py-1 text-xs font-bold"><Pencil className="h-3 w-3" />Edit</button>
                <button onClick={() => del(p.id)} data-testid={`product-del-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border-2 border-brand-ink bg-red-500 px-3 py-1 text-xs font-bold text-white"><Trash2 className="h-3 w-3" />Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Product" : "New Product"}>
        <div className="space-y-3">
          <Field label="Name (EN)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" />
          <Field label="Name (BM)" value={form.name_ms || ""} onChange={(e) => setForm({ ...form, name_ms: e.target.value })} />
          <Area label="Description (EN)" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Area label="Description (BM)" rows={2} value={form.description_ms || ""} onChange={(e) => setForm({ ...form, description_ms: e.target.value })} />
          <label className="block"><span className="text-sm font-semibold">Category</span>
            <input list="pt-cat-list" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" className="mt-1 w-full rounded-lg border-2 border-brand-ink bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-green" />
            <datalist id="pt-cat-list">
              {Array.from(new Set(items.map((x) => x.category).filter(Boolean))).map((c) => <option key={c} value={c} />)}
            </datalist>
          </label>
          <Field label="Display Order (lower shows first)" type="number" value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="product-form-order" />
          <ImagePicker value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} label="Product Photo (4:3)" />
          <button onClick={save} data-testid="product-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save</button>
        </div>
      </Modal>
    </div>
  );
};

const NewsAdmin = ({ refreshStats }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get("/news?all=true").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setForm({ title: "", title_ms: "", content: "", content_ms: "", image_path: "", published: true }); setOpen(true); };
  const save = async () => {
    try {
      if (form.id) await api.put(`/news/${form.id}`, form);
      else await api.post("/news", form);
      toast.success("Saved"); setOpen(false); load(); refreshStats();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/news/${id}`); load(); refreshStats(); };
  return (
    <div data-testid="admin-news">
      <button onClick={openNew} data-testid="news-add" className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Article</button>
      <div className="space-y-3">
        {items.map((n) => (
          <div key={n.id} className="flex items-center gap-4 rounded-2xl border-2 border-brand-ink bg-white p-4">
            {n.image_path && <img src={fileUrl(n.image_path)} alt={n.title} className="h-16 w-24 rounded-lg border-2 border-brand-ink object-cover" />}
            <div className="flex-1">
              <p className="font-display text-lg font-bold">{n.title}</p>
              <p className="text-xs text-muted-foreground">{n.published ? "Published" : "Draft"} · {new Date(n.created_at).toLocaleDateString()}</p>
            </div>
            <button onClick={() => { setForm({ ...n }); setOpen(true); }} data-testid={`news-edit-${n.id}`} className="rounded-lg border-2 border-brand-ink px-3 py-1 text-xs font-bold"><Pencil className="h-3 w-3" /></button>
            <button onClick={() => del(n.id)} data-testid={`news-del-${n.id}`} className="rounded-lg border-2 border-brand-ink bg-red-500 px-3 py-1 text-xs font-bold text-white"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Article" : "New Article"}>
        <div className="space-y-3">
          <Field label="Title (EN)" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-form-title" />
          <Field label="Title (BM)" value={form.title_ms || ""} onChange={(e) => setForm({ ...form, title_ms: e.target.value })} />
          <Area label="Content (EN)" rows={5} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <Area label="Content (BM)" rows={5} value={form.content_ms || ""} onChange={(e) => setForm({ ...form, content_ms: e.target.value })} />
          <ImagePicker value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} label="Cover Image" />
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />Published</label>
          <button onClick={save} data-testid="news-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save</button>
        </div>
      </Modal>
    </div>
  );
};

const Vouchers = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get("/vouchers?all=true").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setForm({ title: "", description: "", points_cost: 100, image_path: "", active: true, min_tier: "bronze", stock: "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, points_cost: parseInt(form.points_cost),
        stock: form.stock === "" || form.stock == null ? null : parseInt(form.stock) };
      if (form.id) await api.put(`/vouchers/${form.id}`, payload);
      else await api.post("/vouchers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/vouchers/${id}`); load(); };
  return (
    <div data-testid="admin-vouchers">
      <button onClick={openNew} data-testid="voucher-add" className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Voucher</button>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((v) => (
          <div key={v.id} className="overflow-hidden rounded-2xl border-2 border-brand-ink bg-white">
            <img src={v.image_path ? fileUrl(v.image_path) : "https://images.unsplash.com/photo-1607013251379-e6eecfffe234?auto=format&fit=crop&w=800&q=80"} alt={v.title} className="aspect-[4/3] w-full object-cover" />
            <div className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-bold">{v.title}</p>
                <span className="rounded-full border-2 border-brand-ink bg-brand-yellow px-2 py-0.5 text-xs font-bold">{v.points_cost}</span>
              </div>
              <p className="line-clamp-1 text-xs text-muted-foreground">{v.description}</p>
              <p className="mt-1 text-xs font-bold">{v.active ? "Active" : "Inactive"} · {v.min_tier || "bronze"} · {v.stock == null ? "∞ stock" : `${v.stock} left`}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => { setForm({ ...v }); setOpen(true); }} data-testid={`voucher-edit-${v.id}`} className="rounded-lg border-2 border-brand-ink px-3 py-1 text-xs font-bold"><Pencil className="h-3 w-3" /></button>
                <button onClick={() => del(v.id)} data-testid={`voucher-del-${v.id}`} className="rounded-lg border-2 border-brand-ink bg-red-500 px-3 py-1 text-xs font-bold text-white"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Voucher" : "New Voucher"}>
        <div className="space-y-3">
          <Field label="Title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="voucher-form-title" />
          <Area label="Description" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Field label="Points Cost" type="number" value={form.points_cost || 0} onChange={(e) => setForm({ ...form, points_cost: e.target.value })} data-testid="voucher-form-points" />
          <label className="block"><span className="text-sm font-semibold">Minimum Tier</span>
            <select value={form.min_tier || "bronze"} onChange={(e) => setForm({ ...form, min_tier: e.target.value })} data-testid="voucher-form-tier" className="mt-1 w-full rounded-lg border-2 border-brand-ink bg-white px-3 py-2 text-sm">
              <option value="bronze">Bronze (everyone)</option>
              <option value="silver">Silver &amp; above</option>
              <option value="gold">Gold only</option>
            </select>
          </label>
          <Field label="Stock limit (blank = unlimited)" type="number" min="0" value={form.stock ?? ""} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="voucher-form-stock" />
          <ImagePicker value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} label="Voucher Image" />
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Active</label>
          <button onClick={save} data-testid="voucher-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save</button>
        </div>
      </Modal>
    </div>
  );
};

const Members = ({ refreshStats }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get("/members").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setForm({ name: "", email: "", phone: "", password: "", points: 0, role: "member" }); setOpen(true); };
  const openEdit = (m) => { setForm({ ...m, password: "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, points: parseInt(form.points) || 0 };
      if (form.id) await api.put(`/members/${form.id}`, payload);
      else await api.post("/members", payload);
      toast.success("Saved"); setOpen(false); load(); refreshStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete member?")) return; try { await api.delete(`/members/${id}`); load(); refreshStats(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div data-testid="admin-members">
      <button onClick={openNew} data-testid="member-add" className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Member</button>
      <div className="overflow-x-auto rounded-2xl border-2 border-brand-ink bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b-2 border-brand-ink bg-brand-cream font-bold">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Points</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr key={m.id} className={i % 2 ? "bg-brand-cream/40" : ""}>
                <td className="p-3 font-semibold">{m.name}</td>
                <td className="p-3">{m.email}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${m.role === "admin" ? "bg-brand-yellow" : "bg-brand-green"}`}>{m.role}</span></td>
                <td className="p-3 font-bold">{m.points ?? 0}</td>
                <td className="p-3 text-right">
                  <button onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`} className="mr-1 rounded-lg border-2 border-brand-ink px-2 py-1 text-xs"><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => del(m.id)} data-testid={`member-del-${m.id}`} className="rounded-lg border-2 border-brand-ink bg-red-500 px-2 py-1 text-xs text-white"><Trash2 className="h-3 w-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Member" : "New Member"}>
        <div className="space-y-3">
          <Field label="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-form-name" />
          <Field label="Email" type="email" value={form.email || ""} disabled={!!form.id} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-form-email" />
          <Field label="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Birthday (MM-DD)" placeholder="e.g. 05-21" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="member-form-birthday" />
          <Field label={form.id ? "New Password (leave blank to keep)" : "Password"} type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-form-password" />
          <Field label="Points" type="number" value={form.points ?? 0} onChange={(e) => setForm({ ...form, points: e.target.value })} data-testid="member-form-points" />
          <label className="block"><span className="text-sm font-semibold">Role</span>
            <select value={form.role || "member"} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-lg border-2 border-brand-ink bg-white px-3 py-2 text-sm">
              <option value="member">member</option><option value="admin">admin</option>
            </select>
          </label>
          <button onClick={save} data-testid="member-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save</button>
        </div>
      </Modal>
    </div>
  );
};

const Stores = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [contactsText, setContactsText] = useState("");
  const load = useCallback(() => api.get("/stores").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setForm({ name: "", address: "", phone: "", map_link: "", image_path: "", opening_time: "10:00", closing_time: "22:00", hours_text: "", contacts: [] }); setContactsText(""); setOpen(true); };
  const openEdit = (s) => { setForm({ ...s }); setContactsText((s.contacts || []).map((c) => `${c.name}, ${c.phone}`).join("\n")); setOpen(true); };
  const save = async () => {
    const contacts = contactsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [name, phone] = l.split(",").map((x) => (x || "").trim());
      return { name: name || "", phone: phone || "" };
    });
    const payload = { ...form, contacts };
    try {
      if (form.id) await api.put(`/stores/${form.id}`, payload);
      else await api.post("/stores", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/stores/${id}`); load(); };
  return (
    <div data-testid="admin-stores">
      <button onClick={openNew} data-testid="store-add" className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Outlet</button>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((s) => (
          <div key={s.id} className="rounded-2xl border-2 border-brand-ink bg-white p-4">
            <p className="font-display text-lg font-bold">{s.name}</p>
            <p className="text-xs text-muted-foreground">{s.address}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => openEdit(s)} data-testid={`store-edit-${s.id}`} className="rounded-lg border-2 border-brand-ink px-3 py-1 text-xs font-bold"><Pencil className="h-3 w-3" /></button>
              <button onClick={() => del(s.id)} data-testid={`store-del-${s.id}`} className="rounded-lg border-2 border-brand-ink bg-red-500 px-3 py-1 text-xs font-bold text-white"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Outlet" : "New Outlet"}>
        <div className="space-y-3">
          <Field label="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-form-name" />
          <Area label="Address" rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="store-form-address" />
          <Field label="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Google Maps Link (optional)" value={form.map_link || ""} onChange={(e) => setForm({ ...form, map_link: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opening Time" type="time" value={form.opening_time || ""} onChange={(e) => setForm({ ...form, opening_time: e.target.value })} data-testid="store-form-open" />
            <Field label="Closing Time" type="time" value={form.closing_time || ""} onChange={(e) => setForm({ ...form, closing_time: e.target.value })} data-testid="store-form-close" />
          </div>
          <Field label="Hours Text (shown to customers, optional)" value={form.hours_text || ""} onChange={(e) => setForm({ ...form, hours_text: e.target.value })} />
          <Area label="Contacts (one per line: Name, Phone)" rows={3} value={contactsText} onChange={(e) => setContactsText(e.target.value)} />
          <ImagePicker value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} label="Outlet Photo" />
          <button onClick={save} data-testid="store-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save</button>
        </div>
      </Modal>
    </div>
  );
};

const Franchise = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/franchise").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (f, status) => {
    try { await api.put(`/franchise/${f.id}`, { status }); load(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this inquiry?")) return; await api.delete(`/franchise/${id}`); load(); };
  const badge = { new: "bg-brand-yellow", contacted: "bg-brand-green", closed: "bg-muted text-muted-foreground" };
  return (
    <div className="space-y-3" data-testid="admin-franchise">
      {items.length === 0 && <p className="text-muted-foreground">No inquiries.</p>}
      {items.map((f) => (
        <div key={f.id} className="rounded-2xl border-2 border-brand-ink bg-white p-4" data-testid={`franchise-${f.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="font-display text-lg font-bold">{f.name}</p>
              <span className={`rounded-full border-2 border-brand-ink px-2 py-0.5 text-xs font-bold uppercase ${badge[f.status || "new"]}`}>{f.status || "new"}</span>
            </div>
            <div className="flex items-center gap-2">
              <a href={`https://wa.me/${(f.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-brand-green px-3 py-1 text-xs font-bold">{f.phone} <ExternalLink className="h-3 w-3" /></a>
              <select value={f.status || "new"} onChange={(e) => setStatus(f, e.target.value)} data-testid={`franchise-status-${f.id}`} className="rounded-lg border-2 border-brand-ink bg-white px-2 py-1 text-xs font-bold">
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
              <button onClick={() => del(f.id)} data-testid={`franchise-del-${f.id}`} className="rounded-lg border-2 border-brand-ink bg-red-500 px-2 py-1 text-xs font-bold text-white"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
          {f.email && <p className="mt-1 text-xs text-muted-foreground">{f.email}</p>}
          <p className="mt-2 text-sm">{f.message}</p>
        </div>
      ))}
    </div>
  );
};

const SettingsPanel = () => {
  const { settings, reloadSettings } = useSettings();
  const [form, setForm] = useState({});
  useEffect(() => { setForm(settings || {}); }, [settings]);
  const save = async () => { try { await api.put("/settings", form); toast.success("Saved"); reloadSettings(); } catch { toast.error("Failed"); } };
  const uploadPdf = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    try { const p = await uploadFile(f); setForm({ ...form, brandbook_path: p }); toast.success("Brand book uploaded"); } catch { toast.error("Failed"); }
  };
  return (
    <div className="max-w-lg space-y-3" data-testid="admin-settings">
      {["facebook", "instagram", "tiktok", "xiaohongshu"].map((k) => (
        <Field key={k} label={`${k[0].toUpperCase()}${k.slice(1)} URL`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`settings-${k}`} />
      ))}
      <Field label="WhatsApp Number (e.g. 60123456789)" value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="settings-whatsapp" />
      <Field label="Franchise WhatsApp Number" value={form.whatsapp_franchise || ""} onChange={(e) => setForm({ ...form, whatsapp_franchise: e.target.value })} />
      <Field label="Contact Email" value={form.email_contact || ""} onChange={(e) => setForm({ ...form, email_contact: e.target.value })} />
      <div>
        <span className="text-sm font-semibold">Brand Book (PDF)</span>
        {form.brandbook_path && <a href={fileUrl(form.brandbook_path)} target="_blank" rel="noreferrer" className="ml-2 text-sm text-brand-greendark underline">current file</a>}
        <input type="file" accept="application/pdf" onChange={uploadPdf} className="mt-1 w-full text-sm" data-testid="settings-brandbook" />
      </div>
      <div className="rounded-2xl border-2 border-brand-ink bg-brand-cream p-4">
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={!!form.promo_enabled} onChange={(e) => setForm({ ...form, promo_enabled: e.target.checked })} data-testid="settings-promo-enabled" />Show homepage promo banner</label>
        <div className="mt-3 space-y-3">
          <Field label="Promo Text (EN)" value={form.promo_text || ""} onChange={(e) => setForm({ ...form, promo_text: e.target.value })} data-testid="settings-promo-text" />
          <Field label="Promo Text (BM)" value={form.promo_text_ms || ""} onChange={(e) => setForm({ ...form, promo_text_ms: e.target.value })} data-testid="settings-promo-text-ms" />
          <Field label="Promo Link (optional)" value={form.promo_link || ""} onChange={(e) => setForm({ ...form, promo_link: e.target.value })} data-testid="settings-promo-link" />
        </div>
      </div>
      <button onClick={save} data-testid="settings-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save Settings</button>
    </div>
  );
};

// ---------- Homepage ----------
const HomePair = ({ form, set, base, label, area }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {area
      ? <Area label={`${label} (EN)`} rows={3} value={form[`${base}_en`] || ""} onChange={(e) => set(`${base}_en`, e.target.value)} data-testid={`home-${base}-en`} />
      : <Field label={`${label} (EN)`} value={form[`${base}_en`] || ""} onChange={(e) => set(`${base}_en`, e.target.value)} data-testid={`home-${base}-en`} />}
    {area
      ? <Area label={`${label} (BM)`} rows={3} value={form[`${base}_ms`] || ""} onChange={(e) => set(`${base}_ms`, e.target.value)} data-testid={`home-${base}-ms`} />
      : <Field label={`${label} (BM)`} value={form[`${base}_ms`] || ""} onChange={(e) => set(`${base}_ms`, e.target.value)} data-testid={`home-${base}-ms`} />}
  </div>
);
const HomeBlock = ({ title, children }) => (
  <div className="rounded-2xl border-2 border-brand-ink bg-white p-4 shadow-brutal">
    <p className="mb-3 font-display text-base font-bold">{title}</p>
    <div className="space-y-3">{children}</div>
  </div>
);
const HomeAdmin = () => {
  const { settings, reloadSettings } = useSettings();
  const [form, setForm] = useState({});
  useEffect(() => { setForm({ ...(settings?.home || {}) }); }, [settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await api.put("/settings", { ...settings, home: form }); toast.success("Saved"); reloadSettings(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="admin-home">
      <div className="rounded-2xl border-2 border-brand-ink bg-brand-cream p-4">
        <p className="font-display text-lg font-bold">Homepage Content</p>
        <p className="text-xs text-muted-foreground">Edit the homepage text and photos. Leave any field blank to use the default.</p>
      </div>
      <HomeBlock title="Hero">
        <HomePair form={form} set={set} base="hero_eyebrow" label="Eyebrow tag" />
        <HomePair form={form} set={set} base="hero_title" label="Title" area />
        <HomePair form={form} set={set} base="hero_sub" label="Subtitle" area />
        <HomePair form={form} set={set} base="hero_cta_menu" label="Menu button" />
        <HomePair form={form} set={set} base="hero_cta_join" label="Join button" />
        <HomePair form={form} set={set} base="hero_badge" label="Yellow badge" />
        <ImagePicker value={form.hero_image} onChange={(p) => set("hero_image", p)} label="Hero Image" />
        <p className="text-xs text-muted-foreground">Recommended: landscape 4:3 (e.g. 1200×900px), JPG/PNG under 2MB.</p>
      </HomeBlock>
      <HomeBlock title="Products section">
        <HomePair form={form} set={set} base="products_sub" label="Small label" />
        <HomePair form={form} set={set} base="products_title" label="Title" />
      </HomeBlock>
      <HomeBlock title="Story card">
        <HomePair form={form} set={set} base="story_title" label="Title" />
        <ImagePicker value={form.story_image} onChange={(p) => set("story_image", p)} label="Story Image" />
        <p className="text-xs text-muted-foreground">Recommended: landscape 16:10 (e.g. 1200×750px), JPG/PNG under 2MB.</p>
      </HomeBlock>
      <HomeBlock title="Franchise card">
        <HomePair form={form} set={set} base="franchise_title" label="Title" />
        <HomePair form={form} set={set} base="franchise_sub" label="Subtitle" area />
        <HomePair form={form} set={set} base="franchise_cta" label="Button" />
        <ImagePicker value={form.franchise_image} onChange={(p) => set("franchise_image", p)} label="Franchise Page Hero Image" />
        <p className="text-xs text-muted-foreground">Shown on the Franchise page header. Recommended: landscape 4:3 (e.g. 1200×900px), JPG/PNG under 2MB.</p>
      </HomeBlock>
      <button onClick={save} data-testid="home-save" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save Homepage</button>
    </div>
  );
};

// ---------- Brand Story ----------
const StoryAdmin = () => {
  const { settings, reloadSettings } = useSettings();
  const [sections, setSections] = useState([]);
  useEffect(() => { setSections((settings?.story_sections || []).map((s) => ({ ...s }))); }, [settings]);
  const update = (i, k, v) => setSections(sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const add = () => setSections([...sections, { text_en: "", text_ms: "", image_path: "" }]);
  const remove = (i) => setSections(sections.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const copy = [...sections];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setSections(copy);
  };
  const save = async () => {
    try { await api.put("/settings", { ...settings, story_sections: sections }); toast.success("Saved"); reloadSettings(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="admin-story">
      <div className="rounded-2xl border-2 border-brand-ink bg-brand-cream p-4">
        <p className="font-display text-lg font-bold">Brand Story Sections</p>
        <p className="text-xs text-muted-foreground">Add, reorder and edit the sections shown on the Brand Story page. Leave all sections empty to use the default story.</p>
      </div>
      {sections.length === 0 && (
        <p className="rounded-2xl border-2 border-dashed border-brand-ink/40 p-6 text-center text-sm text-muted-foreground">No sections yet — using default story. Add one to customise.</p>
      )}
      {sections.map((s, i) => (
        <div key={i} className="rounded-2xl border-2 border-brand-ink bg-white p-4 shadow-brutal" data-testid={`story-section-${i}`}>
          <div className="mb-3 flex items-center justify-between">
            <span className="font-display text-sm font-bold">Section {i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`story-up-${i}`} className="rounded-lg border-2 border-brand-ink p-1 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`story-down-${i}`} className="rounded-lg border-2 border-brand-ink p-1 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => remove(i)} data-testid={`story-del-${i}`} className="rounded-lg border-2 border-brand-ink bg-red-500 p-1 text-white"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Area label="Text (EN)" rows={4} value={s.text_en || ""} onChange={(e) => update(i, "text_en", e.target.value)} data-testid={`story-text-en-${i}`} />
            <Area label="Text (BM)" rows={4} value={s.text_ms || ""} onChange={(e) => update(i, "text_ms", e.target.value)} data-testid={`story-text-ms-${i}`} />
          </div>
          <div className="mt-3">
            <ImagePicker value={s.image_path} onChange={(p) => update(i, "image_path", p)} label="Section Image" />
            <p className="mt-1 text-xs text-muted-foreground">Recommended: landscape 4:3 (e.g. 1200×900px), JPG/PNG under 2MB for sharp, uncropped display.</p>
          </div>
        </div>
      ))}
      <div className="flex gap-3">
        <button onClick={add} data-testid="story-add" className="flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal"><Plus className="h-4 w-4" />Add Section</button>
        <button onClick={save} data-testid="story-save" className="flex-1 rounded-full border-2 border-brand-ink bg-brand-green py-2.5 font-bold hover-brutal">Save Brand Story</button>
      </div>
    </div>
  );
};

// ---------- Shell ----------
export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [stats, setStats] = useState({});
  const refreshStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  const nav = [
    ["overview", "Overview", LayoutDashboard],
    ["receipts", "Receipts", Receipt],
    ["products", "Products", UtensilsCrossed],
    ["news", "News", Newspaper],
    ["vouchers", "Vouchers", Ticket],
    ["members", "Members", Users],
    ["stores", "Outlets", MapPin],
    ["franchise", "Franchise", Inbox],
    ["home", "Homepage", Home],
    ["story", "Brand Story", BookOpen],
    ["settings", "Settings", SettingsIcon],
  ];

  return (
    <div className="flex min-h-screen bg-brand-cream">
      <aside className="hidden w-60 shrink-0 flex-col border-r-2 border-brand-ink bg-white p-4 lg:flex">
        <div className="mb-6 flex items-center gap-2 font-display text-2xl font-medium lowercase">
          <img src="/panda-mascot.png" alt="" className="h-9 w-9 object-contain" /> panda tata
        </div>
        <nav className="flex-1 space-y-1">
          {nav.map(([id, label, Icon]) => (
            <button key={id} data-testid={`admin-nav-${id}`} onClick={() => setSection(id)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${section === id ? "bg-brand-ink text-brand-yellow" : "hover:bg-brand-cream"}`}>
              <Icon className="h-4 w-4" /> {label}
              {id === "receipts" && stats.pending_receipts > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 text-xs text-white">{stats.pending_receipts}</span>}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="mt-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-red-600"><LogOut className="h-4 w-4" />Logout</button>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between border-b-2 border-brand-ink bg-white px-5 py-3">
          <div className="flex gap-2 overflow-x-auto lg:hidden">
            {nav.map(([id, label]) => (
              <button key={id} data-testid={`admin-mobile-nav-${id}`} onClick={() => setSection(id)} className={`whitespace-nowrap rounded-full border-2 border-brand-ink px-3 py-1 text-xs font-bold ${section === id ? "bg-brand-ink text-brand-yellow" : ""}`}>{label}</button>
            ))}
          </div>
          <p className="hidden font-display text-xl font-bold capitalize lg:block">{section}</p>
          <span className="text-sm font-semibold">{user?.name}</span>
        </div>

        <div className="overflow-x-auto p-5">
          {section === "overview" && <Overview stats={stats} />}
          {section === "receipts" && <Receipts refreshStats={refreshStats} />}
          {section === "products" && <Products />}
          {section === "news" && <NewsAdmin refreshStats={refreshStats} />}
          {section === "vouchers" && <Vouchers />}
          {section === "members" && <Members refreshStats={refreshStats} />}
          {section === "stores" && <Stores />}
          {section === "franchise" && <Franchise />}
          {section === "home" && <HomeAdmin />}
          {section === "story" && <StoryAdmin />}
          {section === "settings" && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
